import React, { FC } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { ChevronIcon, MaterialsIcon } from 'src/components/widgets/icons/scene';
import PrintIcon from 'src/shared/material_icons/action/print.svg';
import { panel, PropertiesPanelColorControl, PropertiesPanelLookProvider, PropertiesPanelSwatch } from '../../scene-properties-panel';
import { readMaterialPreview } from '../../scene-editors/materialPreview';
import { fileNameOf, sceneAssetUrl } from '../../scene-editors/sceneUrls';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { setActionSceneElementType } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import { useMaterialPropertyDetector } from '../../../configurator-composer/composer/helpers/materialPropertyDetector';
import { ComposerChoice, ComposerMaterialSlots, ComposerObjectSlots, ComposerSlot, ComposerSlotOption } from '../composerSlots';
import { COMPOSER_LABEL_COLUMN, ComposerFieldNote } from '../composerWorkspace';
import { COMPOSER_ACTION_FAMILIES, getComposerActionCatalog, getComposerActionDefinition, getComposerActionMenuType } from './composerActionCatalog';
import { AdvancedSelect, compactSelectStyles } from 'src/components/widgets/select';
import { DropdownActionItem, DropdownActions } from '../../../../../../widgets/dropdown';
import { ComposerActionGlyph } from './composerActionGlyph';
import {
    ComposerMaterialPropertyType,
    ComposerMaterialsColorType,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    GetActionValueFromType
} from './composerAttributesModels';

// L'AZIONE SI MODIFICA DENTRO LA SUA CARD, SENZA FINESTRE (utente, 2026-10-15; prototipo V2, `actRows`). Prima
// la card apriva un editor con «Action Type» in testa e Save / Cancel in fondo — e le Shared actions lo
// aprivano in una finestra. Ora il tipo si sceglie una volta, quando la card nasce, e ogni riga scrive
// subito: gli oggetti e i materiali sono slot, mostra / nascondi è una scelta a segmenti, il colore è
// quello dello Scene Editor. Il nodo che esce è lo stesso che usciva dalla finestra: il salvataggio non cambia.

// LE RIGHE STANNO NEL CORPO DELLA CARD (ComposerBlock), che porta già il fondo incassato e il respiro.
const Rows = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 8px;
`;

// PRIMA SU CHE COSA, POI CHE COSA (prototipo, set-sep): un filo fra gli input dell'azione e il suo effetto.
const Separator = styled.hr`
    width: 100%;
    margin: 2px 0;
    border: 0;
    border-top: 1px solid var(--editor-sunken, #e9ebed);
`;

// IL NOME A SINISTRA, IL CONTROLLO A DESTRA (prototipo, «Input» / «Action»): la card è stretta e le righe
// sono poche, una colonna sola di nomi le fa leggere come una scheda.
const Field = styled.div<{ $compact?: boolean }>`
    display: grid;
    min-width: 0;
    grid-template-columns: ${COMPOSER_LABEL_COLUMN} minmax(0, 1fr);
    align-items: start;
    column-gap: 10px;

    /* LE ETICHETTE NELLE CARD COME NEL PROTOTIPO (utente, 2026-10-20): 11px, peso normale, grigio tenue. Il grassetto del
       Composer resta nelle sezioni, non dentro le card. */
    > span:first-child {
        padding-top: ${(props) => (props.$compact ? '5px' : '11px')};
        color: var(--editor-faint, #676a6e);
        font-size: 11px;
        font-weight: 400;
        line-height: 16px;
    }

    > div {
        min-width: 0;
    }
`;

const ValueError = styled.div<{ $isError: boolean }>`
    border-radius: 4px;
    ${(props) => props.$isError && 'box-shadow: 0 0 0 1px #de1c22;'}
`;

// IL NOME DELLA RIGA È IL NOME DELLA SUA TENDINA (utente, 2026-10-22, accessibilità): la riga è un gruppo col suo
// nome, e la tendina lo prende da lì (`aria-labelledby`). Prima la tendina aveva solo il segnaposto.
const ActionFieldLabel = React.createContext<string | undefined>(undefined);

const ActionField: FC<{ label: React.ReactNode; compact?: boolean; children: React.ReactNode }> = ({ label, compact, children }) => {
    const labelId = React.useId();
    return (
        <Field $compact={compact} role='group' aria-labelledby={labelId}>
            <span id={labelId}>{label}</span>
            <ActionFieldLabel.Provider value={labelId}>
                <div>{children}</div>
            </ActionFieldLabel.Provider>
        </Field>
    );
};

const splitIds = (value: string) =>
    (value || '')
        .split(';')
        .map((id) => id.trim())
        .filter(Boolean);

const COLOR_TYPES: ComposerSlotOption[] = Object.entries(ComposerMaterialsColorType).map(([label, value]) => ({ value, label }));

// La voce «Center Target» del Target di Move to Camera: non è una camera, è il Reset del pivot.
const CENTER_TARGET = '__center-target__';

// LA MINIATURA DI UNA TEXTURE nello slot del valore.
const TextureThumb = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 4px;
    object-fit: cover;
`;

// LE TEXTURE DELLA SCENA, COME VALORE DI EDIT MATERIAL (utente, 2026-10-21): prima era un campo per incollare un URL. Il
// motore carica il valore così com'è (`new BABYLON.Texture(url)`), quindi ogni voce porta l'URL completo, risolto dalla
// cartella della scena come le miniature dello Scene Editor. Caricare un file nuovo non c'è: serve un posto dove salvarlo.
const useSceneTextureOptions = (current: string): ComposerSlotOption[] => {
    const [zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkScene, store.sceneEditor]);
    const rootUrl: string = zkScene?.get('rootUrl') || '';
    const option = (url: string, label?: string): ComposerSlotOption => ({
        value: url,
        label: fileNameOf(url) || label || url,
        preview: <TextureThumb src={sceneAssetUrl(rootUrl, url)} alt='' />
    });
    const seen = new Set<string>();
    const options: ComposerSlotOption[] = [];
    (sceneEditor?.scene?.textures ?? []).forEach((texture: BABYLON.BaseTexture) => {
        if (texture.isCube || texture instanceof BABYLON.RenderTargetTexture) return;
        const raw: string = texture.metadata?.fileUrl || texture.getInternalTexture?.()?.url || (texture as BABYLON.Texture).url || '';
        // Solo i file immagine: le texture generate dalla scena (il disegno di un'area, i riflessi) non si scelgono qui.
        if (!raw || /^(?:data:|blob:)/i.test(raw) || !/\.(?:png|jpe?g|webp|gif|bmp|tga|dds|ktx2?|basis)(?:[?#]|$)/i.test(raw)) return;
        const url = sceneAssetUrl(rootUrl, raw);
        if (seen.has(url)) return;
        seen.add(url);
        options.push(option(url, texture.name));
    });
    // Un valore salvato che nella scena non c'è più resta visibile, con la sua miniatura.
    if (current && !seen.has(current) && !seen.has(sceneAssetUrl(rootUrl, current))) options.unshift(option(current));
    return options;
};

// UNA TENDINA, NON UNO SLOT (utente, 2026-10-21): lo slot vale quando le voci si distinguono per l'anteprima (oggetti,
// materiali); proprietà, tipo di colore e camere hanno tutte lo stesso segno. Si cerca solo in un elenco lungo; il
// menu esce dalla card.
const ActionSelect: FC<{
    id: string;
    value?: string;
    options: { value: string; label: string }[];
    placeholder: string;
    isError?: boolean;
    onChange: (value: string) => void;
}> = ({ id, value, options, placeholder, isError, onChange }) => (
    <AdvancedSelect
        id={id}
        aria-labelledby={React.useContext(ActionFieldLabel)}
        options={options}
        value={options.find((option) => option.value === value) ?? null}
        onChange={(option: { value: string } | null) => option && onChange(option.value)}
        placeholder={placeholder}
        isError={isError}
        isSearchable={options.length > 8}
        styles={compactSelectStyles as any}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPlacement='auto'
    />
);

/** I valori di partenza di un'azione nuova: «show» e «hide» stanno nel valore, come le scrive il motore. */
export const createComposerAction = (attributeID: string, optionID: string, type: ComposerOptionActionsType, displayOrder: number) => {
    const base = ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
    return setActionSceneElementType({
        ...base,
        attributeID,
        optionID,
        type,
        displayOrder,
        isNew: true,
        selector: '',
        property: '',
        value: type === ComposerOptionActionsType.SHOW_OBJECTS ? 'show' : type === ComposerOptionActionsType.HIDE_OBJECTS ? 'hide' : ''
    });
};

// SU CHE COSA AGISCE OGNI TIPO: cambiando tipo, gli oggetti scelti restano se il nuovo tipo vuole ancora oggetti, e
// così i materiali; il resto (valore, proprietà) si svuota, perché per il nuovo tipo non vorrebbe dire niente.
const selectorKind = (type: ComposerOptionActionsType) => {
    switch (type) {
        case ComposerOptionActionsType.SHOW_OBJECTS:
        case ComposerOptionActionsType.HIDE_OBJECTS:
        case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
        case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
        case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
            return 'objects';
        case ComposerOptionActionsType.SET_MATERIAL_COLOR:
        case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
            return 'materials';
        case ComposerOptionActionsType.MOVE_CAMERA:
            return 'camera';
        default:
            return 'none';
    }
};

/** IL TIPO SI CAMBIA DAL TITOLO DELLA CARD (prototipo, utente 2026-10-20): resta ciò che il nuovo tipo sa usare. */
export const changeComposerActionType = (action: ComposerOptionActionNode, type: ComposerOptionActionsType): ComposerOptionActionNode =>
    setActionSceneElementType({
        ...action,
        type,
        selector: selectorKind(type) === selectorKind(action.type) ? action.selector : '',
        property: '',
        value: type === ComposerOptionActionsType.SHOW_OBJECTS ? 'show' : type === ComposerOptionActionsType.HIDE_OBJECTS ? 'hide' : ''
    });

const TypeButton = styled.button<{ $open?: boolean }>`
    display: flex;
    max-width: 100%;
    align-items: center;
    gap: 4px;
    padding: 0 4px;
    border: 0;
    border-radius: 6px;
    background: none;
    color: ${(props) => (props.$open ? 'var(--editor-accent, #a94700)' : 'var(--editor-fg, #262a30)')};
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    line-height: 22px;
    /* Alto 24, il bersaglio minimo (utente, 2026-10-22, WCAG 2.5.8). */
    min-height: 24px;
    cursor: pointer;

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > svg {
        flex: none;
        width: 12px;
        height: 12px;
        fill: var(--editor-faint, #676a6e);
        opacity: 0;
        transition: opacity 120ms ease;
    }

    &:hover,
    &[aria-expanded='true'] {
        background: var(--editor-sunken, #e9ebed);

        > svg {
            opacity: 1;
        }
    }
`;

const TypePlaceholder = styled.span`
    color: var(--editor-faint, #676a6e);
`;

/**
 * IL MENU DEI TIPI, A FAMIGLIE (prototipo, utente 2026-10-20): Visibility · Materials · Edit material · Camera ·
 * Printing method, separate da un filo, i nomi nostri e niente descrizioni. Serve al titolo di una card e ad Add.
 */
export const composerActionTypeSections = (onPick: (type: ComposerOptionActionsType) => void, current?: ComposerOptionActionsType) => {
    // Le voci accorpate non hanno una riga loro: si sceglie la voce, e nella card si passa dall'una all'altra.
    const catalog = getComposerActionCatalog().filter((definition) => !definition.disabled && !definition.viaMenu);
    const currentEntry = current === undefined ? undefined : getComposerActionMenuType(current);
    return COMPOSER_ACTION_FAMILIES.map((family) =>
        catalog
            .filter((definition) => definition.family === family)
            .map((definition) => (
                <DropdownActionItem
                    key={definition.key}
                    // Il quadratino grigio lo dà il menu degli editor a ogni segno (dropdown.tsx, softListItem).
                    icon={<ComposerActionGlyph icon={definition.icon} />}
                    selected={definition.type === currentEntry}
                    // Rinscegliere la stessa voce non cambia niente: un Hide resta Hide, un Design resta Design.
                    onClick={() => definition.type !== currentEntry && onPick(definition.type as ComposerOptionActionsType)}
                >
                    {definition.name}
                </DropdownActionItem>
            ))
    );
};

/** Il titolo di una card d'azione: il nome del tipo in arancio, che apre il menu dei tipi. */
export const ComposerActionTypeMenu: FC<{ type?: ComposerOptionActionsType; open?: boolean; onChange: (type: ComposerOptionActionsType) => void }> = ({
    type,
    open,
    onChange
}) => (
    <span onClick={(event) => event.stopPropagation()}>
        <DropdownActions
            header={
                // IL NOME CONTIENE QUELLO CHE SI LEGGE (utente, 2026-10-22, WCAG 2.5.3): «Action type: Set Visibility»; prima
                // «Action type» da solo copriva il nome scritto sul bottone.
                <TypeButton
                    type='button'
                    $open={open}
                    aria-label={`${T._('Action type', 'Admin')}: ${type === undefined ? T._('Choose an action…', 'Admin') : getComposerActionDefinition(type).name}`}
                >
                    <span>
                        {type === undefined ? <TypePlaceholder>{T._('Choose an action…', 'Admin')}</TypePlaceholder> : getComposerActionDefinition(type).name}
                    </span>
                    <ChevronIcon />
                </TypeButton>
            }
            sections={composerActionTypeSections(onChange, type)}
            minWidth='236px'
            orientation='LEFT'
        />
    </span>
);

/** L'elenco dei materiali con la loro sfera: la fotografia, quando la scena l'ha già scattata. */
const useMaterialOptions = (materials: BABYLON.Material[]): ComposerSlotOption[] => {
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    return materials.map((material) => ({
        value: material.id,
        label: material.name || material.id,
        preview: <PropertiesPanelSwatch $url={readMaterialPreview(sceneEditor, material.id) ?? undefined} $tint={panel.kindAsset} $size={24} />
    }));
};

interface ComposerActionEditorProps {
    action: ComposerOptionActionNode;
    onChange: (action: ComposerOptionActionNode) => void;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
}

const ComposerActionEditor: FC<ComposerActionEditorProps> = ({ action, onChange, materials = [], meshes = [], cameras = [] }) => {
    const printTypes = useComposerStore((store) => store.printTypes);
    const materialOptions = useMaterialOptions(materials);
    const meshOptions: ComposerSlotOption[] = meshes.map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const cameraOptions: ComposerSlotOption[] = cameras.map((camera) => ({ value: camera.id, label: camera.name || camera.id }));
    const selectorHasError = hasComposerValidationError(
        action,
        ComposerValidationErrorCode.ACTION_SELECTOR_REQUIRED,
        ComposerValidationErrorCode.ACTION_SELECTOR_TOO_LONG
    );
    const valueHasError = hasComposerValidationError(action, ComposerValidationErrorCode.ACTION_VALUE_REQUIRED);
    const propertyHasError = hasComposerValidationError(action, ComposerValidationErrorCode.ACTION_PROPERTY_REQUIRED);

    const patch = (changes: Partial<ComposerOptionActionNode>) => onChange(setActionSceneElementType({ ...action, ...changes }));

    const objects = (
        <ActionField label={T._('Objects', 'Admin')}>
            <ComposerObjectSlots
                id={`composer-action-${action.id}-objects`}
                value={splitIds(action.selector)}
                options={meshOptions}
                allowPatterns
                isError={selectorHasError}
                onChange={(ids) => patch({ selector: ids.join(';') })}
            />
        </ActionField>
    );
    const material = (
        <ActionField label={T._('Material', 'Admin')}>
            <ComposerSlot
                id={`composer-action-${action.id}-material`}
                value={action.value || undefined}
                options={materialOptions}
                glyph={<MaterialsIcon />}
                caption={T._('Material', 'Admin')}
                emptyLabel={T._('Choose a material', 'Admin')}
                searchPlaceholder={T._('Search materials...', 'Admin')}
                noOptionsLabel={T._('The scene has no materials yet.', 'Admin')}
                isError={valueHasError}
                onChange={(value) => patch({ value })}
            />
        </ActionField>
    );

    const rows = () => {
        switch (action.type) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
            case ComposerOptionActionsType.HIDE_OBJECTS:
                return (
                    <>
                        {objects}
                        <Separator />
                        {/* MOSTRA E NASCONDI SONO DUE TIPI NOSTRI, e la scelta passa dall'uno all'altro. */}
                        <ActionField label={T._('Action', 'Admin')} compact>
                            <ComposerChoice
                                ariaLabel={T._('Action', 'Admin')}
                                options={[
                                    { value: ComposerOptionActionsType.SHOW_OBJECTS, label: T._('Show', 'Admin') },
                                    { value: ComposerOptionActionsType.HIDE_OBJECTS, label: T._('Hide', 'Admin') }
                                ]}
                                value={action.type}
                                onChange={(type) => patch({ type, value: type === ComposerOptionActionsType.SHOW_OBJECTS ? 'show' : 'hide' })}
                            />
                        </ActionField>
                    </>
                );
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                return (
                    <>
                        {objects}
                        <Separator />
                        {/* SWAP MATERIAL È UNA VOCE SOLA (utente, 2026-10-21): il materiale dell'oggetto o quello su cui stampa
                            l'area Designer sono due tipi del motore, e la scelta passa dall'uno all'altro tenendo oggetti e materiale. */}
                        <ActionField label={T._('Swap', 'Admin')} compact>
                            <ComposerChoice
                                ariaLabel={T._('Swap', 'Admin')}
                                options={[
                                    { value: ComposerOptionActionsType.SET_OBJECT_MATERIAL, label: T._('Material', 'Admin') },
                                    { value: ComposerOptionActionsType.SET_DESIGN_MATERIAL, label: T._('Design', 'Admin') }
                                ]}
                                value={action.type}
                                onChange={(type) => patch({ type })}
                            />
                        </ActionField>
                        {material}
                    </>
                );
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
                return (
                    <>
                        {objects}
                        <Separator />
                        {material}
                        <ComposerFieldNote>{T._('This action is no longer supported. Replace it with Swap Material.', 'Admin')}</ComposerFieldNote>
                    </>
                );
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
                return (
                    <>
                        <ActionField label={T._('Materials', 'Admin')}>
                            <ComposerMaterialSlots
                                id={`composer-action-${action.id}-materials`}
                                value={splitIds(action.selector)}
                                options={materialOptions}
                                allowPatterns
                                isError={selectorHasError}
                                onChange={(ids) => patch({ selector: ids.join(';') })}
                            />
                        </ActionField>
                        <Separator />
                        <ActionField label={T._('Color type', 'Admin')}>
                            <ActionSelect
                                id={`composer-action-${action.id}-color-type`}
                                value={action.property || undefined}
                                options={COLOR_TYPES}
                                placeholder={T._('Choose a color type', 'Admin')}
                                isError={propertyHasError}
                                onChange={(property) => patch({ property })}
                            />
                        </ActionField>
                        <ActionField label={T._('Color', 'Admin')} compact>
                            <ValueError $isError={valueHasError}>
                                <PropertiesPanelColorControl
                                    id={`composer-action-${action.id}-color`}
                                    value={action.value}
                                    onChange={(value) => patch({ value })}
                                />
                            </ValueError>
                        </ActionField>
                    </>
                );
            case ComposerOptionActionsType.MOVE_CAMERA:
            case ComposerOptionActionsType.SET_CAMERA_PIVOT:
            case ComposerOptionActionsType.RESET_CAMERA_PIVOT: {
                // MOVE TO CAMERA È UNA VOCE SOLA (utente, 2026-10-21): Camera porta la vista a una camera salvata, il
                // Target sposta il punto attorno a cui gira; «Center Target» lo rimette al centro. Tre tipi del motore.
                const isCamera = action.type === ComposerOptionActionsType.MOVE_CAMERA;
                return (
                    <>
                        <ActionField label={T._('Move', 'Admin')} compact>
                            <ComposerChoice
                                ariaLabel={T._('Move', 'Admin')}
                                options={[
                                    { value: 'camera', label: T._('Camera', 'Admin') },
                                    { value: 'target', label: T._('Target', 'Admin') }
                                ]}
                                value={isCamera ? 'camera' : 'target'}
                                onChange={(choice) =>
                                    patch({
                                        type: choice === 'camera' ? ComposerOptionActionsType.MOVE_CAMERA : ComposerOptionActionsType.SET_CAMERA_PIVOT,
                                        selector: '',
                                        value: ''
                                    })
                                }
                            />
                        </ActionField>
                        {isCamera ? (
                            <ActionField label={T._('Camera', 'Admin')}>
                                <ActionSelect
                                    id={`composer-action-${action.id}-camera`}
                                    value={action.selector || undefined}
                                    options={cameraOptions}
                                    placeholder={T._('Choose a camera', 'Admin')}
                                    isError={selectorHasError || valueHasError}
                                    onChange={(selector) => patch({ selector })}
                                />
                            </ActionField>
                        ) : (
                            <ActionField label={T._('Target', 'Admin')}>
                                <ActionSelect
                                    id={`composer-action-${action.id}-pivot`}
                                    value={action.type === ComposerOptionActionsType.RESET_CAMERA_PIVOT ? CENTER_TARGET : action.value || undefined}
                                    options={[{ value: CENTER_TARGET, label: T._('Center Target', 'Admin') }, ...cameraOptions]}
                                    placeholder={T._('Choose a target', 'Admin')}
                                    isError={action.type === ComposerOptionActionsType.SET_CAMERA_PIVOT && valueHasError}
                                    onChange={(value) =>
                                        patch(
                                            value === CENTER_TARGET
                                                ? { type: ComposerOptionActionsType.RESET_CAMERA_PIVOT, value: '' }
                                                : { type: ComposerOptionActionsType.SET_CAMERA_PIVOT, value }
                                        )
                                    }
                                />
                            </ActionField>
                        )}
                    </>
                );
            }
            case ComposerOptionActionsType.SET_PRINTING_METHOD:
                return (
                    <ActionField label={T._('Printing method', 'Admin')}>
                        <ComposerSlot
                            id={`composer-action-${action.id}-printing`}
                            value={action.value || undefined}
                            options={printTypes.map((printType) => ({
                                value: String(printType.printTypeID),
                                label: printType.name || String(printType.printTypeID)
                            }))}
                            glyph={<PrintIcon />}
                            caption={T._('Printing method', 'Admin')}
                            emptyLabel={T._('Choose a printing method', 'Admin')}
                            searchPlaceholder={T._('Search printing methods…', 'Admin')}
                            noOptionsLabel={T._('No printing methods are available for this account.', 'Admin')}
                            isError={valueHasError}
                            onChange={(value) => patch({ value })}
                        />
                    </ActionField>
                );
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                return (
                    <MaterialParameterRows
                        action={action}
                        materials={materials}
                        materialOptions={materialOptions}
                        selectorHasError={selectorHasError}
                        propertyHasError={propertyHasError}
                        valueHasError={valueHasError}
                        onChange={onChange}
                    />
                );
            default:
                return <ComposerFieldNote>{T._('Unsupported action type selected.', 'Admin')}</ComposerFieldNote>;
        }
    };

    return (
        <PropertiesPanelLookProvider value='scene'>
            <Rows>{rows()}</Rows>
        </PropertiesPanelLookProvider>
    );
};

export default ComposerActionEditor;

type SelectedProperty = { value: string; type: ComposerMaterialPropertyType; label?: string };

// «CHANGE A MATERIAL SETTING» TIENE LA SUA STRADA (materiale → gruppo → proprietà → eventuale sotto-proprietà →
// valore), la stessa della finestra di prima e con le stesse regole per leggere e riscrivere il nome della
// proprietà: la notazione col punto, i vecchi nomi col trattino basso, il caso della rugosità del clear coat.
// Cambia il modo: le scelte sono slot, e ogni passo scrive subito l'azione invece di aspettare «Save».
const MaterialParameterRows: FC<{
    action: ComposerOptionActionNode;
    materials: BABYLON.Material[];
    materialOptions: ComposerSlotOption[];
    selectorHasError: boolean;
    propertyHasError: boolean;
    valueHasError: boolean;
    onChange: (action: ComposerOptionActionNode) => void;
}> = ({ action, materials, materialOptions, selectorHasError, propertyHasError, valueHasError, onChange }) => {
    const [parsedAction, setParsedAction] = React.useState<MPlaza.AttributeSceneAction>(() => ComposerAttributeNodeAdapter.fromActionNode(action));
    const [selectedMaterial, setSelectedMaterial] = React.useState<BABYLON.Material | null>(
        () => materials.find((candidate) => candidate.id === action.selector) || null
    );
    const [selectedProperty, setSelectedProperty] = React.useState('');
    const [selectedSubProperty, setSelectedSubProperty] = React.useState<SelectedProperty | null>(null);
    const [selectedSubSubProperty, setSelectedSubSubProperty] = React.useState<SelectedProperty | null>(null);
    const [propertyName, setPropertyName] = React.useState('');
    const { getAvailablePropertyGroups, renderPropertyControl, hasSubSubProperties, getSubSubProperties } = useMaterialPropertyDetector(selectedMaterial);
    const hasInitializedFromAction = React.useRef(false);
    // Solo un gesto dell'utente riscrive l'azione: aprire la card non deve cambiarla.
    const dirty = React.useRef(false);

    // LA ROUGHNESS DEL CLEAR COAT: o la texture, o «usa quella della texture principale». Si legge dall'azione
    // salvata, altrimenti dal materiale.
    const [useRoughnessFromMainTexture, setUseRoughnessFromMainTexture] = React.useState<boolean>(() => {
        const propertyPath = action.property;
        if (propertyPath === 'clearCoat.useRoughnessFromMainTexture') return action.value === 'true';
        if (propertyPath === 'clearCoat_textureRoughnessTexture') return false;
        const clearCoat = (selectedMaterial as any)?.clearCoat;
        return clearCoat && typeof clearCoat.useRoughnessFromMainTexture !== 'undefined' ? clearCoat.useRoughnessFromMainTexture : true;
    });

    // RICOSTRUIRE LE SCELTE DA UN'AZIONE GIÀ SALVATA, una volta sola, quando il materiale ha detto i suoi gruppi.
    React.useEffect(() => {
        if (hasInitializedFromAction.current || !selectedMaterial || !getAvailablePropertyGroups.length) return;
        const propertyPath = action.property || '';
        if (!propertyPath) return;
        const pick = (group: string, sub: SelectedProperty, subSub: SelectedProperty | null, name: string) => {
            setSelectedProperty(group);
            setSelectedSubProperty(sub);
            setSelectedSubSubProperty(subSub);
            setPropertyName(name);
            hasInitializedFromAction.current = true;
        };

        let parsedGroup = '';
        let parsedSub = '';
        if (propertyPath.includes('.')) {
            const parts = propertyPath.split('.');
            parsedGroup = parts[0].toLowerCase();
            parsedSub = parts.slice(1).join('.');
        } else if (propertyPath.includes('_')) {
            const parts = propertyPath.split('_');
            parsedGroup = parts[0].toLowerCase();
            parsedSub = parts.slice(1).join('_');
        }

        for (const group of getAvailablePropertyGroups) {
            if (parsedGroup && group.value.toLowerCase() !== parsedGroup) continue;
            for (const sub of group.subProperties) {
                if (parsedSub && sub.value === parsedSub) return pick(group.value, sub, null, parsedSub);
                if (sub.value === propertyPath) return pick(group.value, sub, null, propertyPath);
                if (!hasSubSubProperties(group.value, sub.value)) continue;
                for (const subSub of getSubSubProperties(group.value, sub.value)) {
                    const lastPart = subSub.value.split('.').pop();
                    if (subSub.value === propertyPath || (parsedSub && (subSub.value === parsedSub || (subSub.value.includes('.') && lastPart === parsedSub))))
                        return pick(group.value, sub, subSub, parsedSub);
                }
            }
        }

        // I CASI SPECIALI, come li riconosceva la finestra.
        if (
            (propertyPath.includes('clearCoat') && propertyPath.includes('useRoughnessFromMainTexture')) ||
            (propertyPath.includes('clearCoat') && propertyPath.includes('RoughnessTexture'))
        ) {
            const clearCoat = getAvailablePropertyGroups.find((group) => group.value === 'clearcoat');
            const roughness = clearCoat?.subProperties.find((sub) => sub.value === 'clearCoat_roughness_section');
            if (roughness) {
                const checkbox = getSubSubProperties('clearcoat', 'clearCoat_roughness_section').find(
                    (subSub) => subSub.value === 'clearCoat.useRoughnessFromMainTexture'
                );
                setUseRoughnessFromMainTexture(propertyPath === 'clearCoat.useRoughnessFromMainTexture' && action.value === 'true');
                return pick('clearcoat', roughness, checkbox ?? null, parsedSub || propertyPath);
            }
        }
        if (propertyPath.includes('sheen') && /roughness|texture|Roughness/.test(propertyPath)) {
            const sheen = getAvailablePropertyGroups.find((group) => group.value === 'sheen');
            const sub =
                propertyPath.includes('sheen_texture') || propertyPath.includes('sheen.texture')
                    ? sheen?.subProperties.find((candidate) => candidate.value === 'sheen_texture')
                    : sheen?.subProperties.find((candidate) => candidate.value.includes('roughness') || candidate.value.includes('Roughness'));
            if (sheen && sub) {
                const subSub = hasSubSubProperties('sheen', sub.value)
                    ? getSubSubProperties('sheen', sub.value).find(
                          (candidate) => candidate.value === propertyPath || (!!parsedSub && candidate.value.includes(parsedSub))
                      )
                    : undefined;
                return pick('sheen', sub, subSub ?? null, parsedSub || propertyPath);
            }
        }
        if ((propertyPath.includes('anisotrop') && propertyPath.includes('texture')) || propertyPath === 'anisotropy_texture') {
            const anisotropic = getAvailablePropertyGroups.find((group) => group.value === 'anisotropic' || group.value === 'anisotropy');
            const sub = anisotropic?.subProperties.find((candidate) => candidate.value.includes('texture'));
            if (anisotropic && sub) return pick(anisotropic.value, sub, null, parsedSub || propertyPath);
        }
        if (/subsurface/i.test(propertyPath) && /texture/i.test(propertyPath)) {
            const subSurface = getAvailablePropertyGroups.find((group) => group.value.toLowerCase() === 'subsurface');
            const legacy = propertyPath.replace('.', '_');
            const sub = subSurface?.subProperties.find(
                (candidate) =>
                    candidate.value === propertyPath ||
                    candidate.value === parsedSub ||
                    (!!parsedSub && candidate.value.includes(parsedSub)) ||
                    candidate.value === legacy
            );
            if (subSurface && sub) return pick(subSurface.value, sub, null, parsedSub || propertyPath);
        }
    }, [action.property, action.value, selectedMaterial, getAvailablePropertyGroups, hasSubSubProperties, getSubSubProperties]);

    // OGNI PASSO SCRIVE L'AZIONE, con la stessa ricetta che la finestra applicava a «Save»: il materiale nel
    // selettore, il nome della proprietà col punto e senza prefissi doppi, solo il valore nel valore.
    React.useEffect(() => {
        if (!dirty.current) return;
        dirty.current = false;
        const value = (parsedAction.get('value') as string) || '';
        let fullPropertyName = propertyName;
        const prefixed = propertyName.toLowerCase().startsWith(`${selectedProperty.toLowerCase()}.`);
        if (selectedProperty && propertyName && !prefixed) fullPropertyName = `${selectedProperty}.${propertyName}`;
        parsedAction.set('selector', selectedMaterial?.id ?? '');
        parsedAction.set('propertyName', fullPropertyName);
        parsedAction.set('value', value);
        parsedAction.set('actionTypeID', GetActionValueFromType(ComposerOptionActionsType.SET_MATERIAL_PARAMETER));
        setActionSceneElementType(parsedAction);
        const next = ComposerAttributeNodeAdapter.toNodeAction(action.attributeID, action.optionID, parsedAction);
        onChange({
            ...action,
            type: next.type,
            sceneElementType: next.sceneElementType,
            selector: next.selector,
            property: next.property,
            value: next.value
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedAction]);

    const update = () => {
        dirty.current = true;
        setParsedAction((current) => current.clone());
    };
    const setValue = (value: string) => {
        if (propertyName === 'clearCoat.useRoughnessFromMainTexture') setUseRoughnessFromMainTexture(value === 'true');
        parsedAction.set('value', value || '');
    };
    const updatePropertyName = (value: string) => {
        setPropertyName(value);
        parsedAction.set('propertyName', value);
    };

    const textureOptions = useSceneTextureOptions((parsedAction.get('value') as string) || '');
    const groups = getAvailablePropertyGroups;
    const subProperties = groups.find((group) => group.value === selectedProperty)?.subProperties ?? [];
    const subSubProperties =
        selectedSubProperty && hasSubSubProperties(selectedProperty, selectedSubProperty.value)
            ? getSubSubProperties(selectedProperty, selectedSubProperty.value)
            : [];
    const currentProperty = selectedSubSubProperty || (selectedSubProperty && !subSubProperties.length ? selectedSubProperty : null);
    const propertySlot = (
        id: string,
        label: string,
        options: { value: string; label: string }[],
        value: string | undefined,
        emptyLabel: string,
        onPick: (value: string) => void
    ) => (
        <ActionField label={label}>
            <ActionSelect
                id={`composer-action-${action.id}-${id}`}
                value={value || undefined}
                options={options.map((option) => ({ value: option.value, label: option.label }))}
                placeholder={emptyLabel}
                isError={propertyHasError}
                onChange={onPick}
            />
        </ActionField>
    );

    // Che cosa manca ancora, detto in chiaro sotto le righe (era il messaggio della finestra a «Save»).
    const missing = !selectedMaterial
        ? T._('Material is required.', 'Admin')
        : !selectedProperty
          ? T._('Property group is required.', 'Admin')
          : !selectedSubProperty
            ? T._('Sub-property is required.', 'Admin')
            : subSubProperties.length && !selectedSubSubProperty
              ? T._('Sub-sub-property is required.', 'Admin')
              : '';

    const valueControl = () => {
        if (!currentProperty) return null;
        const isRoughness = selectedProperty === 'clearcoat' && selectedSubProperty?.value === 'clearCoat_roughness_section';
        const rawValue = (parsedAction.get('value') as string) || '';
        if (!isRoughness && currentProperty.type === ComposerMaterialPropertyType.TEXTURE)
            return (
                <ComposerSlot
                    id={`composer-action-${action.id}-texture`}
                    value={rawValue || undefined}
                    options={textureOptions}
                    glyph={<ImageIcon />}
                    caption={T._('Texture', 'Admin')}
                    emptyLabel={T._('Choose a texture', 'Admin')}
                    searchPlaceholder={T._('Search textures…', 'Admin')}
                    noOptionsLabel={T._('The scene has no textures yet.', 'Admin')}
                    isError={valueHasError}
                    clearable
                    onChange={(value) => {
                        setValue(value);
                        update();
                    }}
                />
            );
        const valueToPass = isRoughness
            ? JSON.stringify({
                  checkboxValue: useRoughnessFromMainTexture,
                  textureValue: useRoughnessFromMainTexture || rawValue === 'false' ? '' : rawValue
              })
            : rawValue;
        return (
            <ValueError $isError={valueHasError}>
                {renderPropertyControl(
                    { label: currentProperty.value.split('.').pop() || currentProperty.value, value: currentProperty.value, type: currentProperty.type },
                    valueToPass,
                    (newValue) => {
                        // Il controllo della rugosità risponde con un prefisso: «checkbox:» o «texture:».
                        if (newValue.startsWith('checkbox:')) {
                            const actualValue = newValue.replace('checkbox:', '').split('|')[0];
                            updatePropertyName('clearCoat.useRoughnessFromMainTexture');
                            setUseRoughnessFromMainTexture(actualValue === 'true');
                            setValue(actualValue);
                        } else if (newValue.startsWith('texture:')) {
                            setUseRoughnessFromMainTexture(false);
                            updatePropertyName('clearCoat_textureRoughnessTexture');
                            setValue(newValue.replace('texture:', ''));
                        } else {
                            setValue(newValue);
                        }
                        update();
                    }
                )}
            </ValueError>
        );
    };

    return (
        <>
            <ActionField label={T._('Material', 'Admin')}>
                <ComposerSlot
                    id={`composer-action-${action.id}-material`}
                    value={selectedMaterial?.id}
                    options={materialOptions}
                    glyph={<MaterialsIcon />}
                    caption={T._('Material', 'Admin')}
                    emptyLabel={T._('Choose a material', 'Admin')}
                    searchPlaceholder={T._('Search materials...', 'Admin')}
                    noOptionsLabel={T._('The scene has no materials yet.', 'Admin')}
                    isError={selectorHasError}
                    onChange={(materialID) => {
                        const next = materials.find((candidate) => candidate.id === materialID) || null;
                        setSelectedMaterial(next);
                        setSelectedProperty('');
                        setSelectedSubProperty(null);
                        setSelectedSubSubProperty(null);
                        updatePropertyName('');
                        setValue('');
                        const clearCoat = (next as any)?.clearCoat;
                        if (clearCoat && typeof clearCoat.useRoughnessFromMainTexture !== 'undefined')
                            setUseRoughnessFromMainTexture(clearCoat.useRoughnessFromMainTexture);
                        update();
                    }}
                />
            </ActionField>
            {selectedMaterial &&
                propertySlot('group', T._('Property', 'Admin'), groups, selectedProperty, T._('Choose a property', 'Admin'), (value) => {
                    setSelectedProperty(value);
                    setSelectedSubProperty(null);
                    setSelectedSubSubProperty(null);
                    updatePropertyName('');
                    setValue('');
                    update();
                })}
            {selectedMaterial &&
                selectedProperty &&
                propertySlot('sub', T._('Sub-property', 'Admin'), subProperties, selectedSubProperty?.value, T._('Choose a sub-property', 'Admin'), (value) => {
                    const picked = subProperties.find((candidate) => candidate.value === value);
                    if (!picked) return;
                    setSelectedSubProperty({ value: picked.value, type: picked.type });
                    setSelectedSubSubProperty(null);
                    updatePropertyName(hasSubSubProperties(selectedProperty, picked.value) ? '' : picked.value);
                    // La rugosità del clear coat riparte da come la ha il materiale.
                    if (selectedProperty === 'clearcoat' && picked.value === 'clearCoat_roughness_section') {
                        const clearCoat = (selectedMaterial as any)?.clearCoat;
                        if (clearCoat && typeof clearCoat.useRoughnessFromMainTexture !== 'undefined')
                            setUseRoughnessFromMainTexture(clearCoat.useRoughnessFromMainTexture);
                    }
                    setValue('');
                    update();
                })}
            {subSubProperties.length > 0 &&
                selectedSubProperty &&
                propertySlot(
                    'subsub',
                    `${selectedSubProperty.value.split('_')[0]} ${T._('sub-property', 'Admin')}`,
                    subSubProperties,
                    selectedSubSubProperty?.value,
                    T._('Choose a sub-property', 'Admin'),
                    (value) => {
                        const picked = subSubProperties.find((candidate) => candidate.value === value);
                        if (!picked) return;
                        setSelectedSubSubProperty({ value: picked.value, type: picked.type });
                        updatePropertyName(picked.value);
                        setValue('');
                        update();
                    }
                )}
            {currentProperty && <ActionField label={T._('Value', 'Admin')}>{valueControl()}</ActionField>}
            {missing && <ComposerFieldNote>{missing}</ComposerFieldNote>}
        </>
    );
};
